import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useContext, useEffect, useState } from "react";

const formatPacificDateTime = (value: any) => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

type Props = { residentId: string; medicationId: string };

const PrnMedicationHistory = ({ residentId, medicationId }: Props) => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [entries, setEntries] = useState<any[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const response = await backendAPIStore.getResidentEmarPrnHistory(residentId);
        if (active) setCount(Number(response?.countsByMedicationId?.[medicationId] || 0));
      } catch { if (active) setCount(0); }
    })();
    return () => { active = false; };
  }, [backendAPIStore, medicationId, residentId]);

  const load = async (offset = 0) => {
    setLoading(true);
    try {
      const response = await backendAPIStore.getResidentEmarPrnHistory(residentId, { medicationId, limit: 25, offset });
      setCount(Number(response?.total || 0));
      setEntries((current) => offset ? [...current, ...(response?.items || [])] : (response?.items || []));
      setNextOffset(response?.nextOffset ?? null);
    } finally { setLoading(false); }
  };

  if (!count) return null;
  return <>
    <div className="mt-2 inline-flex align-items-center" style={{ border: "1px solid #bae6fd", borderRadius: "999px", background: "#f0f9ff", padding: "0.15rem 0.6rem" }}>
      <Button type="button" link size="small" className="p-0" label={`Charted ${count} time${count === 1 ? "" : "s"} before · View history`} onClick={() => { setVisible(true); void load(); }} />
    </div>
    <Dialog
      header={<div><div style={{ color: "#0f172a", fontWeight: 800 }}>Today&apos;s PRN Entries</div><div style={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500, marginTop: "0.2rem" }}>Today&apos;s medication administration history</div></div>}
      visible={visible}
      onHide={() => setVisible(false)}
      style={{ width: "min(50rem, 96vw)" }}
      headerStyle={{ borderBottom: "1px solid #e2e8f0", padding: "1rem 1.15rem" }}
      contentStyle={{ background: "#f8fafc", padding: "1rem 1.15rem 1.15rem" }}
    >
      {loading && entries.length === 0 ? <div className="py-4 text-center text-color-secondary">Loading previous PRN entries...</div> : null}
      {!loading && entries.length === 0 ? <div className="py-4 text-center text-color-secondary">No PRN administrations recorded today.</div> : null}
      <div className="flex flex-column gap-3">
        {entries.map((entry: any) => {
          const fields = [
            ["Reason", entry?.prnReasonLabel || entry?.prnReasonCode],
            ["Effectiveness", entry?.prnEffectivenessLabel || entry?.prnEffectiveness],
            ["Side effects", entry?.prnSideEffectLabel || entry?.prnSideEffectCode],
            ["Follow-up", entry?.prnFollowUpNote],
            ["Side effect note", entry?.prnSideEffectNote],
            ["Notes", entry?.note],
          ].filter(([, value]) => String(value || "").trim());
          return <div key={entry?.entryId || `${entry?.medicationId}-${entry?.recordedAt}`} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", background: "#fff", padding: "0.7rem 0.8rem" }}>
            <div className="flex justify-content-between align-items-start gap-2 flex-wrap" style={{ color: "#0f172a", fontSize: "0.86rem", fontWeight: 700 }}>
              <span>{formatPacificDateTime(entry?.recordedAt || entry?.createdAt)}</span>
              {entry?.recordedByName ? <span style={{ color: "#64748b", fontWeight: 600 }}>{entry.recordedByName}</span> : null}
            </div>
            <div className="mt-2 flex flex-column gap-1">
              {fields.map(([label, value]) => <div key={label as string} style={{ color: "#334155", fontSize: "0.84rem", lineHeight: 1.35, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                <strong style={{ color: "#64748b" }}>{label}:</strong> {String(value)}
              </div>)}
            </div>
          </div>;
        })}
      </div>
      {nextOffset !== null ? <div className="mt-3 flex justify-content-end"><Button type="button" label="Load more" outlined loading={loading} onClick={() => void load(nextOffset)} /></div> : null}
    </Dialog>
  </>;
};

export default PrnMedicationHistory;
